import type { ActorKind, AuditActor, AuditEntry } from '@/api/generated';

/**
 * Who did something, and what it was done to, in words.
 *
 * Kept apart from the table and the drawer so the two say it the same way,
 * and so the account page's panel can say it too.
 */

/** The kind of person, as the drawer and the table's hover text say it. */
export const ACTOR_KIND_LABELS: Record<ActorKind, string> = {
  ACTIVIST_STAFF: 'Activist staff',
  ORGANIZATION_USER: 'Organization user',
  SYSTEM: 'System',
  // An app user acting on their own support or notifications. Their entries
  // carry no name or address, so this label is all that can be said; they are
  // never offered under Done by (decided 5 October).
  CONSUMER: 'App user',
};

/**
 * The Done by choices, in the order the filter lists them. App users are left
 * out on purpose: their support and notification switches are not
 * administrative actions, and the portal does not offer them (decided
 * 5 October).
 */
export const DONE_BY_OPTIONS: { value: ActorKind; label: string }[] = [
  { value: 'ACTIVIST_STAFF', label: 'Activist staff' },
  { value: 'ORGANIZATION_USER', label: 'Organization users' },
  { value: 'SYSTEM', label: 'System' },
];

/**
 * The person's name, or "System" for a write made on nobody's behalf.
 *
 * A person whose name the log did not keep falls back to their address, then
 * to the kind, so the cell is never blank.
 */
export function actorLabel(actor: AuditActor): string {
  if (actor.kind === 'SYSTEM') return 'System';
  return actor.name || actor.email || ACTOR_KIND_LABELS[actor.kind] || 'Unknown';
}

/**
 * A person as the Person filter lists them: the name, then the address, then
 * the kind. The address tells apart two people with the same name, who would
 * otherwise be two identical choices. Left off when the log kept no address,
 * or when the address is already standing in for a missing name.
 */
export function personOptionLabel(actor: AuditActor): string {
  const name = actorLabel(actor);
  const address = actor.email && actor.email !== name ? ` · ${actor.email}` : '';
  return `${name}${address} (${ACTOR_KIND_LABELS[actor.kind]})`;
}

/**
 * Who did it, as the organization's own view says it: Activist staff are
 * named only as "Activist staff", never by name. The organization deals with
 * Activist Technologies, not with a particular employee.
 */
export function actorLabelFor(actor: AuditActor, hideStaffNames: boolean): string {
  return hideStaffNames && actor.kind === 'ACTIVIST_STAFF'
    ? ACTOR_KIND_LABELS.ACTIVIST_STAFF
    : actorLabel(actor);
}

/** Whether a row names the kind under the person: not for staff shown as a kind. */
export function showsKindLine(actor: AuditActor, hideStaffNames: boolean): boolean {
  if (actor.kind === 'SYSTEM') return false;
  return !(hideStaffNames && actor.kind === 'ACTIVIST_STAFF');
}

/** The words for a target's type. */
const TARGET_TYPE_LABELS: Record<string, string> = {
  organization: 'Organization',
  portal_user: 'User',
  arc: 'ARC',
  campaign: 'Campaign',
  document: 'Document',
  document_category: 'Document category',
  fundraising_link: 'Fundraising link',
  volunteer_link: 'Volunteer link',
  page: 'Page',
};

export function targetTypeLabel(type: string | null | undefined): string {
  if (!type) return '';
  return TARGET_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

/** What the entry was about, as one short line. */
export function targetLabel(entry: AuditEntry): string {
  return entry.target.label || targetTypeLabel(entry.target.type) || '—';
}

/** The organization an entry belongs to; "Platform" for one that belongs to none. */
export function organizationLabel(entry: AuditEntry): string {
  return entry.organization?.name ?? 'Platform';
}
